import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Customer } from '@/types/customer-type';
import { Calendar, MapPin, Phone, ShoppingBag } from 'lucide-react';
import { formatDate } from '../utils/format-date';

interface CustomerInfoCardProps {
  customer: Customer;
}

export function CustomerInfoCard({ customer }: CustomerInfoCardProps) {
  return (
    <Card className="md:col-span-1">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center text-center mb-6">
          <Avatar className="h-24 w-24 mb-4">
            <AvatarImage src={customer.profileImageUrl || ''} alt={customer.name} />
            <AvatarFallback className="bg-blue-100 text-blue-600 text-xl">
              {customer.name.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <h2 className="text-2xl font-bold text-gray-900">{customer.name}</h2>

          <div className="flex items-center gap-2 mt-2">
            {customer.notTransfer && (
              <Badge variant="outline" className="border-amber-300 text-amber-600">
                Not Transfer
              </Badge>
            )}
          </div>

          <p className="text-gray-500 mt-3 flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>Customer since {formatDate(customer.createdAt)}</span>
          </p>
        </div>

        <Separator className="my-4" />

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Phone Number</p>
              <p className="font-medium">{customer.phone || 'Not provided'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Address</p>
              <p className="font-medium whitespace-pre-line">
                {customer.address || 'Not provided'}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <ShoppingBag className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Sales Channel</p>
              <p className="font-medium">{customer.saleChannel || 'Not specified'}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
